import { QueryClient, dehydrate, hydrate } from "@tanstack/react-query";

// Single shared QueryClient. Extracted from App so the prerender step can seed
// it and read its state.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep results fresh for a minute so route changes don't refetch.
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Prerender integration (build-time only; a no-op for normal visitors):
//   • If a prerendered page embedded its react-query cache (window.__REACT_QUERY_STATE__,
//     injected by scripts/prerender.mjs), seed it BEFORE the app's first render so
//     the client renders the same content immediately, no empty→data flash.
//   • Expose hooks the prerender script uses to serialize the cache into the page
//     and to know when all queries have settled before snapshotting.
if (typeof window !== "undefined") {
  const w = window as unknown as {
    __REACT_QUERY_STATE__?: unknown;
    __DEHYDRATE_QUERY__?: () => unknown;
    __QUERY_FETCHING__?: () => number;
  };
  if (w.__REACT_QUERY_STATE__) {
    try {
      hydrate(queryClient, w.__REACT_QUERY_STATE__ as never);
      // The embedded data is from the last release. Show it at once, but
      // fetch the live data as each query mounts, so an admin's edit (a
      // collection, an area, site text) is on the page right away instead of
      // waiting out a long staleTime or the next release.
      void queryClient.invalidateQueries({ refetchType: "none" });
    } catch {
      /* ignore malformed embedded state */
    }
  }
  w.__DEHYDRATE_QUERY__ = () => dehydrate(queryClient);
  w.__QUERY_FETCHING__ = () => queryClient.isFetching();
}
